import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import downloadPDF from "./DownloadReport";
import downloadSLACertificate from "./SLAreport";
import ErrorNotification from "../ErrorNotification";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  CircularProgress,
  Typography,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  ChevronDown,
  BarChart3,
  Award,
  Check,
  Calendar,
  X,
} from "lucide-react";
import { createTheme, ThemeProvider, styled, alpha } from "@mui/material/styles";

// Clean theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#003366",
      light: "#1a4d80",
      dark: "#001a33",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#0066cc",
      light: "#3385d6",
      dark: "#004d99",
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
    text: {
      primary: "#003366",
      secondary: "#6c757d",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          borderRadius: 4,
        },
      },
    },
  },
});

// Custom styled components
const Container = styled(Box)({
  padding: "16px 20px",
  backgroundColor: "#ffffff",
});

// Toggle from EndpointFilters - replicated exactly
const DurationToggleContainer = styled(Box)({
  display: "flex",
  bgcolor: "#f5f5f5",
  borderRadius: 2,
  p: 0.5,
  mr: 2,
});

const CustomFormControl = styled(FormControl)({
  minWidth: 120,
  "& .MuiInputLabel-root": {
    fontSize: "0.8rem",
    color: "#808080",
    fontWeight: 500,
    "&.Mui-focused": {
      color: "#003366",
    },
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 4,
    backgroundColor: "#ffffff",
    height: 36,
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#cccccc",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#003366",
      borderWidth: 1,
    },
  },
  "& .MuiSelect-select": {
    fontSize: "0.8rem",
    color: "#003366",
    fontWeight: 500,
    padding: "8px 12px",
  },
});

const CustomMenuItem = styled(MenuItem)({
  fontSize: "0.8rem",
  color: "#003366",
  padding: "8px 14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  "&:hover": {
    backgroundColor: "rgba(0, 51, 102, 0.05)",
  },
  "&.Mui-selected": {
    backgroundColor: "#f0f4f8",
    "&:hover": {
      backgroundColor: "#e6f0f7",
    },
  },
});

const DownloadButton = styled(Button)({
  backgroundColor: "#003366",
  borderRadius: 4,
  padding: "8px 18px",
  minWidth: 110,
  height: 36,
  fontSize: "0.8rem",
  fontWeight: 500,
  color: "#ffffff",
  border: "none",
  boxShadow: "none",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#001a33",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  "&:disabled": {
    background: "#e0e0e0",
    color: "#9e9e9e",
  },
});

const StyledMenu = styled(Menu)({
  "& .MuiPaper-root": {
    borderRadius: 4,
    marginTop: 6,
    minWidth: 280, // Changed from 180 to 280
    border: "1px solid #e0e0e0",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    "& .MuiMenu-list": {
      padding: 0, // Changed from padding: 6 to padding: 0
    },
    "& .MuiMenuItem-root": {
      borderRadius: 0, // Changed from borderRadius: 4
      margin: "0", // Changed from margin: "1px 0"
      padding: "6px 10px",
    },
  },
});

const DatePickerDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: 8,
    minWidth: 320,
    maxWidth: 400,
  },
});

const MonthYearGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "8px",
  padding: "16px 0",
});

const MonthButton = styled(Button)({
  borderRadius: 4,
  padding: "8px 12px",
  fontSize: "0.8rem",
  textTransform: "none",
  border: "1px solid #e0e0e0",
  color: "#003366",
  backgroundColor: "#ffffff",
  "&:hover": {
    backgroundColor: "#f0f4f8",
    borderColor: "#003366",
  },
  "&.selected": {
    backgroundColor: "#003366",
    color: "#ffffff",
    borderColor: "#003366",
  },
});

const YearButton = styled(Button)({
  borderRadius: 4,
  padding: "6px 12px",
  fontSize: "0.8rem",
  textTransform: "none",
  border: "1px solid #e0e0e0",
  color: "#003366",
  backgroundColor: "#ffffff",
  minWidth: "60px",
  "&:hover": {
    backgroundColor: "#f0f4f8",
    borderColor: "#003366",
  },
  "&.selected": {
    backgroundColor: "#003366",
    color: "#ffffff",
    borderColor: "#003366",
  },
});

export default function EnhancedFilterComponent(Props) {
  const {
    oem,
    setOem,
    product,
    setProduct,
    environment,
    setEnvironment,
    apiType,
    setApiType,
    duration,
    setDuration,
  } = Props;

  const [oemDetails, setOemDetails] = useState({});
  const [endpointTypeDetails, setEndpointTypeDetails] = useState({});
  const [productDetails, setProductDetails] = useState({});
  const [environmentDetails, setEnvironmentDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [oemOptions, setOemOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [environmentOptions, setEnvironmentOptions] = useState([]);
  const [endpointTypeOptions, setEndpointTypeOptions] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [downloadDuration, setDownloadDuration] = useState(duration);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [customDate, setCustomDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 2 }, (_, i) => currentYear - i);
  const [useCustomDate, setUseCustomDate] = useState(false);
  let accessToken = useSelector((state) => state.accessToken.value);
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const query = `
      query GetDetails($id: String!) {
        getDetails(id: $id) {
          id
          oem
          region
          environment
          product
        }
      }
    `;

        const variables = {
          id: "1",
        };
        const response = await axios.post(
          apiUrl,
          { query, variables },
          {
            headers: {
              Authorization: accessToken,
            },
          }
        );
        if (
          response.data.errors &&
          response.data.errors[0].errorType == "Unauthorized"
        )
          Props.setError("Unauthorized");
        if (response.data.data.getDetails) {

          const configurationDetails = JSON.parse(response.request.response);
          const oem = JSON.parse(configurationDetails.data.getDetails.oem);
          const region = JSON.parse(configurationDetails.data.getDetails.region);
          const product = JSON.parse(
            configurationDetails.data.getDetails.product
          );
          const environment = JSON.parse(
            configurationDetails.data.getDetails.environment
          );

          const query1 = `
          query MyQuery($id:String!) {
            getApiType(id: $id) {
              ApiType
              }
              }
              `;

          const variables1 = {
            id: "2",
          };
          const response1 = await axios.post(
            apiUrl,
            { query: query1, variables: variables1 },
            {
              headers: {
                Authorization: accessToken,
              },
            }
          );
          const configurationDetails1 = JSON.parse(response1.request.response);
          const endpointType = JSON.parse(
            configurationDetails1.data.getApiType.ApiType
          );
          setEndpointTypeOptions([...Object.keys(endpointType), "all"]);
          setOemOptions([...Object.keys(oem), "all"]);
          setProductOptions([...Object.keys(product), "all"]);
          setEnvironmentOptions([...Object.keys(environment), "all"]);
          setOemDetails(oem);
          setProductDetails(product);
          setEnvironmentDetails(environment);
          setEndpointTypeDetails(endpointType);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const handleDownloadClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDatePicker = () => {
    setDatePickerOpen(true);
  };

  const handleCloseDatePicker = () => {
    setDatePickerOpen(false);
  };

  const handleApplyCustomDate = () => {
    setUseCustomDate(true);
    // Format: MM-YYYY (01-2025 format)
    const formattedMonth = String(customDate.month + 1).padStart(2, '0');
    const formattedDuration = `${formattedMonth}-${customDate.year}`;
    console.log("Custom Duration Selected:", formattedDuration);
    setDownloadDuration(formattedDuration);
    setDatePickerOpen(false);
  };

  const handleResetToToggle = () => {
    setUseCustomDate(false);
    setDownloadDuration(duration);
    setDatePickerOpen(false);
  };
  const getDisplayText = () => {
    if (useCustomDate) {
      return `${months[customDate.month]} ${customDate.year}`;
    }
    return downloadDuration === "7days" ? "7 Days" : downloadDuration === "1month" ? "1 Month" : "3 Months";
  };

  const handleDownload = async (downloadFunction) => {
    setIsLoading(true);
    handleDownloadClose();
    console.log("Download initiated with duration:", downloadDuration);
    const oemDescription =
      oemDetails[oem] || Object.values(oemDetails).join(", ");
    const environmentDescription =
      environmentDetails[environment] ||
      Object.values(environmentDetails).join(", ");
    const productDescription =
      productDetails[product] || Object.values(productDetails).join(",");
    try {
      console.log("Download initiated with duration:", downloadDuration);
      await downloadFunction(
        oem,
        product,
        environment,
        apiType,
        downloadDuration,
        apiUrl,
        accessToken,
        oemDescription,
        environmentDescription,
        productDescription,
        oemDetails,
        productDetails,
        environmentDetails,
        endpointTypeDetails
      );
    } catch (error) {
      console.error("Download error:", error);
      setErrorMessage('No endpoint data found for the selected period. Please try a different time range or filter combination.');
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 5000);

    } finally {
      setIsLoading(false);
    }
  };

  const renderSelectValue = (value, options, details) => {
    if (value === "all") {
      return <span style={{ color: "#6c757d" }}>All</span>;
    }
    return <span style={{ color: "#003366", fontWeight: 500 }}>{value}</span>;
  };

  const renderMenuItem = (item, selectedValue) => (
    <CustomMenuItem key={item} value={item}>
      <span style={{
        color: item === "all" ? "#6c757d" : "#003366",
        fontWeight: item === selectedValue ? 600 : 500
      }}>
        {item === "all" ? "All" : item}
      </span>
      {item === selectedValue && (
        <Check size={14} style={{ color: "#28a745" }} />
      )}
    </CustomMenuItem>
  );

  return (
    <ThemeProvider theme={theme}>
      {showError && (
        <ErrorNotification
          message={errorMessage}
          onClose={() => setShowError(false)}
        />
      )}
      <Container>
        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}>
          {/* Duration Toggle - Replicated from EndpointFilters */}
          <Box
            sx={{
              display: "flex",
              bgcolor: "#f5f5f5",
              borderRadius: 2,
              p: 0.5,
              mr: 2,
            }}
          >
            {["7days", "1month", "3months"].map((option) => (
              <Button
                key={option}
                onClick={() => {
                  localStorage.setItem('duration', option);
                  setDuration(option);
                }}
                variant={duration === option ? "contained" : "text"}
                size="small"
                sx={{
                  mx: 0.5,
                  textTransform: "none",
                  minWidth: "auto",
                  height: 32, // Slightly lesser height than dropdown (36px)
                  bgcolor: duration === option ? "primary.main" : "transparent",
                  "&:hover": {
                    bgcolor:
                      duration === option
                        ? "primary.dark"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                {option === "7days"
                  ? "7 Days"
                  : option === "1month"
                    ? "1 Month"
                    : "3 Months"}
              </Button>
            ))}
          </Box>

          {/* Filter Dropdowns */}
          <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
            <CustomFormControl size="small">
              <InputLabel>OEM</InputLabel>
              <Select
                value={oem}
                onChange={(e) => {
                  const value = e.target.value;
                  setOem(value);
                  localStorage.setItem('oem', value);
                }}
                label="OEM"
                renderValue={(value) => renderSelectValue(value, oemOptions, oemDetails)}
              >
                {oemOptions.map((item) => renderMenuItem(item, oem))}
              </Select>
            </CustomFormControl>

            <CustomFormControl size="small">
              <InputLabel>Product</InputLabel>
              <Select
                value={product}
                onChange={(e) => {
                  const value = e.target.value;
                  setProduct(value);
                  localStorage.setItem('product', value);
                }}
                label="Product"
                renderValue={(value) => renderSelectValue(value, productOptions, productDetails)}
              >
                {productOptions.map((item) => renderMenuItem(item, product))}
              </Select>
            </CustomFormControl>

            <CustomFormControl size="small">
              <InputLabel>Environment</InputLabel>
              <Select
                value={environment}
                onChange={(e) => {
                  const value = e.target.value;
                  setEnvironment(value);
                  localStorage.setItem('environment', value);
                }}
                label="Environment"
                renderValue={(value) => renderSelectValue(value, environmentOptions, environmentDetails)}
              >
                {environmentOptions.map((item) => renderMenuItem(item, environment))}
              </Select>
            </CustomFormControl>

            <CustomFormControl size="small">
              <InputLabel>API Type</InputLabel>
              <Select
                value={apiType}
                onChange={(e) => {
                  const value = e.target.value;
                  setApiType(value);
                  localStorage.setItem('apiType', value);
                }}
                label="API Type"
                renderValue={(value) => renderSelectValue(value, endpointTypeOptions, endpointTypeDetails)}
              >
                {endpointTypeOptions.map((item) => renderMenuItem(item, apiType))}
              </Select>
            </CustomFormControl>
          </Box>

          {/* Download Button */}
          <Box sx={{ ml: "auto" }}>
            <DownloadButton
              variant="contained"
              onClick={handleDownloadClick}
              disabled={isLoading}
              endIcon={isLoading ? null : <ChevronDown size={16} />}
              startIcon={
                isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {isLoading ? "Processing..." : "Download"}
            </DownloadButton>
          </Box>
        </Box>


        <StyledMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleDownloadClose}
          TransitionComponent={Fade}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {/* Compact Date Range Section */}
          <Box sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #f0f0f0",
            bgcolor: "#fafafa"
          }}>
            {/* Header with current selection */}
            <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.25
            }}>
              <Typography variant="body2" sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "#003366"
              }}>
                Date Range
              </Typography>
              <Box sx={{
                px: 1.5,
                py: 0.5,
                bgcolor: useCustomDate ? "#e3f2fd" : "#f0f4f8",
                borderRadius: 3,
                border: `1px solid ${useCustomDate ? "#90caf9" : "#e0e0e0"}`
              }}>
                <Typography variant="caption" sx={{
                  fontSize: "0.7rem",
                  color: useCustomDate ? "#1976d2" : "#003366",
                  fontWeight: 500
                }}>
                  {getDisplayText()}
                </Typography>
              </Box>
            </Box>

            {/* Compact Controls Row */}
            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              justifyContent: "space-between"
            }}>
              {/* Quick Select Pills */}
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {["7days", "1month", "3months"].map((option) => (
                  <Button
                    key={option}
                    onClick={() => {
                      setDownloadDuration(option);
                      setUseCustomDate(false);
                    }}
                    variant="text"
                    size="small"
                    sx={{
                      fontSize: "0.68rem",
                      textTransform: "none",
                      minWidth: "32px",
                      height: 22,
                      px: 1,
                      borderRadius: 3,
                      bgcolor: downloadDuration === option && !useCustomDate ? "#003366" : "rgba(0, 51, 102, 0.08)",
                      color: downloadDuration === option && !useCustomDate ? "#ffffff" : "#003366",
                      fontWeight: downloadDuration === option && !useCustomDate ? 600 : 500,
                      border: "none",
                      "&:hover": {
                        bgcolor: downloadDuration === option && !useCustomDate ? "#001a33" : "rgba(0, 51, 102, 0.15)",
                      },
                    }}
                  >
                    {option === "7days" ? "7D" : option === "1month" ? "1M" : "3M"}
                  </Button>
                ))}
              </Box>

              {/* Custom Date Button */}
              <Button
                onClick={handleOpenDatePicker}
                startIcon={<Calendar size={12} />}
                variant="text"
                size="small"
                sx={{
                  fontSize: "0.68rem",
                  textTransform: "none",
                  height: 22,
                  px: 1.25,
                  borderRadius: 3,
                  bgcolor: useCustomDate ? "#003366" : "rgba(0, 51, 102, 0.08)",
                  color: useCustomDate ? "#ffffff" : "#003366",
                  fontWeight: useCustomDate ? 600 : 500,
                  border: "none",
                  "& .MuiButton-startIcon": {
                    marginRight: "4px",
                  },
                  "&:hover": {
                    bgcolor: useCustomDate ? "#001a33" : "rgba(0, 51, 102, 0.15)",
                  },
                }}
              >
                Custom
              </Button>
            </Box>
          </Box>

          {/* Streamlined Download Options */}
          <Box sx={{ py: 1 }}>
            <MenuItem
              onClick={() => handleDownload(downloadPDF)}
              sx={{
                px: 2,
                py: 1.25,
                "&:hover": {
                  bgcolor: "rgba(0, 51, 102, 0.04)"
                }
              }}
            >
              <Box sx={{
                display: "flex",
                alignItems: "center",
                width: "100%"
              }}>
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  bgcolor: "rgba(0, 51, 102, 0.1)",
                  mr: 1.5
                }}>
                  <BarChart3 size={16} style={{ color: "#003366" }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    color: "#003366",
                    lineHeight: 1.2
                  }}>
                    Download Report
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: "#6c757d",
                    fontSize: "0.68rem",
                    lineHeight: 1.2
                  }}>
                    Analytics report
                  </Typography>
                </Box>
              </Box>
            </MenuItem>

            <MenuItem
              onClick={() => handleDownload(downloadSLACertificate)}
              sx={{
                px: 2,
                py: 1.25,
                "&:hover": {
                  bgcolor: "rgba(0, 51, 102, 0.04)"
                }
              }}
            >
              <Box sx={{
                display: "flex",
                alignItems: "center",
                width: "100%"
              }}>
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  bgcolor: "rgba(255, 152, 0, 0.1)",
                  mr: 1.5
                }}>
                  <Award size={16} style={{ color: "#ff9800" }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    color: "#003366",
                    lineHeight: 1.2
                  }}>
                    SLO Certificate
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: "#6c757d",
                    fontSize: "0.68rem",
                    lineHeight: 1.2
                  }}>
                    Service level objectives
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          </Box>
        </StyledMenu>

        <DatePickerDialog
          open={datePickerOpen}
          onClose={handleCloseDatePicker}
          maxWidth="sm"
        >
          <DialogTitle sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1
          }}>
            <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
              Select Custom Date Range
            </Typography>
            <IconButton onClick={handleCloseDatePicker} size="small">
              <X size={18} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ pt: 1 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{
                mb: 2,
                fontSize: "0.85rem",
                fontWeight: 500,
                color: "#003366"
              }}>
                Select Month
              </Typography>
              <MonthYearGrid>
                {months.map((month, index) => (
                  <MonthButton
                    key={month}
                    onClick={() => setCustomDate(prev => ({ ...prev, month: index }))}
                    className={customDate.month === index ? "selected" : ""}
                  >
                    {month}
                  </MonthButton>
                ))}
              </MonthYearGrid>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{
                mb: 2,
                fontSize: "0.85rem",
                fontWeight: 500,
                color: "#003366"
              }}>
                Select Year
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {years.map((year) => (
                  <YearButton
                    key={year}
                    onClick={() => setCustomDate(prev => ({ ...prev, year }))}
                    className={customDate.year === year ? "selected" : ""}
                  >
                    {year}
                  </YearButton>
                ))}
              </Box>
            </Box>

            <Box sx={{
              mt: 3,
              p: 2,
              bgcolor: "#f8f9fa",
              borderRadius: 1,
              border: "1px solid #e9ecef"
            }}>
              <Typography variant="body2" sx={{
                fontSize: "0.8rem",
                color: "#003366",
                fontWeight: 500
              }}>
                Selected: {months[customDate.month]} {customDate.year}
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button
              onClick={handleResetToToggle}
              sx={{
                color: "#6c757d",
                fontSize: "0.8rem",
                textTransform: "none"
              }}
            >
              Use Toggle
            </Button>
            <Button
              onClick={handleApplyCustomDate}
              variant="contained"
              sx={{
                fontSize: "0.8rem",
                textTransform: "none",
                bgcolor: "#003366",
                "&:hover": {
                  bgcolor: "#001a33"
                }
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </DatePickerDialog>
      </Container>
    </ThemeProvider>
  );
}